import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getWorkOrders, getAssets, getUsers, createWorkOrder, uploadWorkOrderAttachment } from '../../services/api';
import { PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { WorkOrderDetailDialog } from '../../components/WorkOrderDetail';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Calendar, MapPin, Wrench, ChevronRight, Plus, Camera, X, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { QrScanner } from '../../components/QrScanner';

const MyWorkOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [detailWo, setDetailWo] = useState(null);
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const blankForm = { title: '', description: '', priority: 'medium', category: 'Mechanical', assetId: '', customerId: '' };
  const [form, setForm] = useState(blankForm);
  // Photos the engineer wants to attach to the new WO. Uploaded sequentially
  // AFTER the WO is created (the attachments endpoint needs a WO id).
  const [photos, setPhotos] = useState([]); // Array<File>

  const onPhotoPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotos(prev => [...prev, ...files]);
    // Reset the input so picking the same file twice still fires onChange.
    e.target.value = '';
  };
  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  // ── QR scanner: engineers point the camera at the asset's QR plate.
  // Asset QRs encode `/qr/request/{siteId}?asset={assetId}` (see QrCodes.jsx),
  // so we parse the URL, pull the `asset` param, and auto-fill the dropdown.
  const [scanOpen, setScanOpen] = useState(false);
  const handleScan = (decoded) => {
    setScanOpen(false);
    let assetId = null;
    try {
      const url = new URL(decoded);
      assetId = url.searchParams.get('asset');
    } catch {
      // Plain assetId string from another QR generator — accept it verbatim.
      if (decoded && !decoded.includes(' ')) assetId = decoded.trim();
    }
    if (!assetId) {
      toast.error('QR code did not contain an asset reference');
      return;
    }
    const match = assets.find(a => a.id === assetId);
    if (!match) {
      toast.error('Asset not found on this account');
      return;
    }
    // Mirror the manual-pick path: auto-fill customer from the
    // asset's owner so the engineer doesn't have to set it twice.
    setForm(f => ({
      ...f,
      assetId: match.id,
      customerId: match.customerId || f.customerId,
    }));
    toast.success(`Linked to ${match.name}`);
  };

  const refresh = () => getWorkOrders().then(w => setOrders(w.filter(o => o.assigneeId === user.id || (o.assigneeIds || []).includes(user.id))));

  useEffect(() => {
    refresh();
    // Employees also need asset/customer pick-lists to raise a new job from the field.
    getAssets().then(setAssets).catch(() => {});
    getUsers().then(u => setCustomers(u.filter(x => x.role === 'customer'))).catch(() => {});
  }, [user.id]);

  const create = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      // Backend auto-assigns to the current engineer when assigneeId is blank.
      const wo = await createWorkOrder({ ...form, assigneeId: user.id });
      // Best-effort upload photos. If one fails we surface the error but keep
      // going — the WO itself was already created so rolling back would leave
      // orphaned toasts either way.
      if (photos.length) {
        for (const p of photos) {
          try { await uploadWorkOrderAttachment(wo.id, p); }
          catch (e) { toast.error(`Could not attach ${p.name}`); }
        }
      }
      await refresh();
      setForm(blankForm);
      setPhotos([]);
      setOpen(false);
      toast.success(photos.length ? `Work order created with ${photos.length} photo${photos.length === 1 ? '' : 's'}` : 'Work order created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="My Work Orders"
        description="Jobs assigned to you, sorted by priority. Tap a card to open and complete it."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="employee-new-wo-btn">
                <Plus className="h-4 w-4 mr-2" /> New Work Order
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Raise Work Order</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    className="mt-1.5"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Replace conveyor motor"
                    data-testid="employee-wo-title"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    className="mt-1.5"
                    rows={3}
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="What needs doing? Any safety notes, parts seen on-site, etc."
                    data-testid="employee-wo-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="employee-wo-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="employee-wo-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mechanical">Mechanical</SelectItem>
                        <SelectItem value="Electrical">Electrical</SelectItem>
                        <SelectItem value="PPM">PPM</SelectItem>
                        <SelectItem value="Inspection">Inspection</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Asset (optional)</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-700 hover:text-blue-800"
                      onClick={() => setScanOpen(true)}
                      data-testid="employee-wo-scan-qr-btn"
                    >
                      <ScanLine className="h-3.5 w-3.5 mr-1" /> Scan QR
                    </Button>
                  </div>
                  <Select
                    value={form.assetId}
                    onValueChange={v => setForm(f => {
                      // Mirror the admin WorkOrders flow: picking an
                      // asset auto-fills the customer from the asset's
                      // owner so engineers don't have to set it twice.
                      const a = assets.find(x => x.id === v);
                      return {
                        ...f,
                        assetId: v,
                        customerId: a?.customerId || f.customerId,
                      };
                    })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="employee-wo-asset"><SelectValue placeholder="Select asset" /></SelectTrigger>
                    <SelectContent>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Customer (optional)</Label>
                  <Select value={form.customerId} onValueChange={v => setForm({ ...form, customerId: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="employee-wo-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Photos (optional)</Label>
                  <div className="mt-1.5 space-y-2">
                    {/* Label acts as the click target so we can trigger the
                        camera on mobile via `capture="environment"` without
                        exposing the ugly default file input styling. */}
                    <label
                      htmlFor="employee-wo-photo-input"
                      className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-slate-400 hover:bg-slate-50 cursor-pointer"
                      data-testid="employee-wo-photo-picker"
                    >
                      <Camera className="h-4 w-4" />
                      <span>Add photo(s) — tap to use camera or pick from gallery</span>
                    </label>
                    <input
                      id="employee-wo-photo-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={onPhotoPick}
                      data-testid="employee-wo-photo-input"
                    />
                    {photos.length > 0 && (
                      <div className="grid grid-cols-3 gap-2" data-testid="employee-wo-photo-previews">
                        {photos.map((p, i) => (
                          <div key={i} className="relative group">
                            {/* Preview via createObjectURL. We don't revoke
                                because the dialog tears down on close — React
                                will GC the inner <img> and the browser will
                                clean the URL on dialog close/page nav. */}
                            <img
                              src={URL.createObjectURL(p)}
                              alt={p.name}
                              className="h-20 w-full object-cover rounded border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              className="absolute top-1 right-1 p-1 bg-white/90 border border-slate-200 rounded-full hover:bg-white"
                              aria-label={`Remove ${p.name}`}
                              data-testid={`employee-wo-photo-remove-${i}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Assignee: <span className="font-medium text-slate-700">{user.name} (you)</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  onClick={create}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="employee-wo-submit"
                >
                  {saving ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid md:grid-cols-2 gap-4">
        {orders.map(w => (
          <button
            key={w.id}
            type="button"
            onClick={() => setDetailWo(w)}
            data-testid={`my-wo-card-${w.id}`}
            className="bg-white border border-slate-200 rounded-md p-5 hover:border-blue-700 hover:shadow-md active:bg-slate-50 transition-all text-left w-full"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><span className="text-xs font-mono text-slate-500">{w.id}</span><PriorityBadge priority={w.priority} /></div>
              <StatusBadge status={w.status} />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">{w.title}</h3>
            <p className="text-sm text-slate-600 mb-3 line-clamp-2">{w.description}</p>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex items-center gap-2"><Wrench className="h-3.5 w-3.5" /> {w.asset || '—'}</div>
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {w.customer || '—'}</div>
              <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> Due {w.dueDate || '—'}</div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">{w.attachments?.length ? `${w.attachments.length} attachment${w.attachments.length === 1 ? '' : 's'}` : 'No attachments'}</span>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700">
                Open <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </button>
        ))}
        {orders.length === 0 && <div className="col-span-full p-12 text-center text-slate-500 bg-white border border-slate-200 rounded-md">No work orders assigned. Tap <span className="font-medium text-slate-700">New Work Order</span> above to raise one.</div>}
      </div>
      <WorkOrderDetailDialog
        wo={detailWo}
        open={!!detailWo}
        onOpenChange={(v) => { if (!v) setDetailWo(null); }}
        onUpdate={refresh}
      />
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleScan}
        title="Scan Asset QR"
        hint="Point your camera at the QR plate on the machine to auto-fill the Asset field."
      />
    </div>
  );
};

export default MyWorkOrders;
